/**
 * Разговор со SKOKA: вопрос, уточнение, расчёт, предложение.
 *
 * Модальный лист, а не блок на главной, по двум причинам. Разговор занимает
 * экран целиком — на телефоне с открытой клавиатурой места нет ни на что
 * другое. И разговор продолжается: закрыть его, чтобы посмотреть на числа, и
 * вернуться обратно — обычное дело, поэтому состояние живёт снаружи и
 * закрытие его не уничтожает. Неотправленный вопрос — тоже.
 *
 * Главное здесь — граница между гипотезой и фактом. Разбор считает «что
 * будет, если»; ничего в данных человека он не меняет. Изменение происходит
 * только по кнопке в карточке «Что изменится», только из посчитанного и
 * только пока данные с тех пор не изменились. «Не сейчас» — полноправный
 * ответ, а не отказ от разговора.
 *
 * Рисует окно `chat/chat-view.tsx`; здесь — разговор: что спросить, что
 * показать и что сделать по нажатию.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ZERO, formatMoney, previewGoalChange, type AdvisorAnswer, type Goal, type Money,
  type PlainDate, type Proposal,
} from '@skoka/core';
import { advisorGate, dayLabel, fetchAccess, onAccessChanged, type AccessState } from './access.js';
import { askAdvisor, contextOf, stateVersion, type ClarifyTurn } from './advisor.js';
import {
  ChatSheet, type ChatAccess, type ChatMsg, type PastTalk as PastRow, type PlanChange,
} from './chat/chat-view.js';
import { chatAnswerOf } from './chat/answer.js';
import { ConditionsSheet } from './chat/conditions-sheet.js';
import { examplesFor } from './chat/examples.js';
import { SheetWrap } from './sheet.js';
import { keepMonthPlan } from './plan-version.js';
import { loadState, updateGoal, type AppState } from './store.js';
import { dayMonth, dayMonthYear, pluralWord } from './text.js';
import { track } from './track.js';

/** Одна реплика разговора. */
export type Turn =
  | {
    who: 'you'; text: string; at?: string;
    /** Человек попросил разбор кнопкой, а не словами: в модель вопрос не уходит. */
    review?: boolean;
  }
  | { who: 'skoka'; kind: 'question'; text: string; at?: string; options?: string[] }
  | {
    who: 'skoka'; kind: 'answer'; answer: AdvisorAnswer;
    proposal?: Proposal; version: string;
    /** Когда посчитан. Разбор без даты нельзя отличить от вчерашнего. */
    at?: string;
    /** Человек его ещё не видел. Прежняя метка «новый»; больше не рисуется. */
    fresh?: boolean;
    /** Это разбор картины, а не ответ на вопрос. */
    review?: boolean;
    /**
     * Карточку убрали ответом «не сейчас».
     *
     * Убирается именно эта карточка, а не предложение вообще: спросить о том
     * же ещё раз человек вправе.
     */
    hidden?: boolean;
    /** Предложение применено — когда. Второй раз его не применить. */
    applied?: string;
    /**
     * Что показывала карточка в момент применения.
     *
     * После применения цель уже другая, и пересчёт «было → стало» по ней
     * давал «взнос 30 000 → 30 000». Применённое показывается таким, каким
     * его подтвердили.
     */
    planShown?: { what: string; rows: PlanChange['rows']; note?: string };
  }
  | {
    who: 'skoka'; kind: 'note'; text: string; at?: string;
    /**
     * Модель не ответила — можно повторить тот же ход.
     *
     * Отдельной репликой, а не запасным ответом: шаблон вместо модели
     * человек читает как ответ на свой вопрос.
     */
    retry?: { question?: string; answering?: string; review?: boolean };
  };

/** Прошлый разговор: целиком, только для чтения. */
export interface PastTalk { id: string; at: string; turns: Turn[] }

/**
 * Разговор целиком.
 *
 * Живёт вне модального листа: закрытие окна не должно стирать начатое — ни
 * уточнение, на которое человек ещё не ответил, ни предложение, которое он
 * обдумывает, ни недописанный вопрос.
 */
export interface Talk {
  turns: Turn[];
  /** Ключ разбора и заданные уточнения — память между запросами. */
  analysisId?: string;
  history: ClarifyTurn[];
  /**
   * Вопрос, с которого начался текущий разбор.
   *
   * Ответ на уточнение — не новый вопрос: модель получает исходный вопрос и
   * уточнения к нему. Прежде уходил только ответ — «17 октября», — и модель,
   * не зная про шкаф за 140 000, спрашивала, о какой покупке речь.
   */
  asked?: string;
  /** Неотправленный вопрос. */
  draft?: string;
  /** Прошлые разговоры — в «Истории разборов», а не над новым вопросом. */
  past?: PastTalk[];
}

export const emptyTalk = (): Talk => ({ turns: [], history: [] });

/** Сколько прошлых разговоров хранить. */
const PAST_LIMIT = 20;

/**
 * Когда был последний разбор.
 *
 * Нужно главной: «Последний разбор · сегодня, 18:42» отвечает на вопрос
 * «стоит ли смотреть снова» раньше, чем человек откроет окно. Ищется и в
 * прошлых разговорах: начатый новый разговор разбор не отменяет.
 */
export const lastReviewAt = (talk: Talk): string | undefined => {
  for (const turns of [talk.turns, ...(talk.past ?? []).map((p) => p.turns)]) {
    for (let i = turns.length - 1; i >= 0; i -= 1) {
      const t = turns[i]!;
      if (t.who === 'skoka' && t.kind === 'answer' && t.at) return t.at;
    }
  }
  return undefined;
};

/** Убрать текущий разговор в историю и начать новый. Ничего не удаляется. */
export function archiveTalk(talk: Talk): Talk {
  const { analysisId: _a, asked: _q, ...rest } = talk;
  if (talk.turns.length === 0) return { ...rest, history: [] };
  const at = talk.turns.find((t) => t.at)?.at ?? new Date().toISOString();
  const past: PastTalk = { id: `${at}-${talk.turns.length}`, at, turns: talk.turns };
  return { ...rest, turns: [], history: [], past: [past, ...(talk.past ?? [])].slice(0, PAST_LIMIT) };
}

/** Разговор вчерашний: при открытии он уходит в историю сам. */
const isOld = (talk: Talk, today: string): boolean => {
  const last = [...talk.turns].reverse().find((t) => t.at)?.at;
  return Boolean(last) && localDay(last!) < today;
};

const localDay = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const now = (): string => new Date().toISOString();

/** Время в «Применено · 09:20». */
const clock = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
};

const REVIEW_ASK = 'Разбери мои деньги';

/** Скрыть карточку одной реплики: ответ «не сейчас» относится к ней одной. */
const patchAt = (
  talk: Talk, i: number,
  patch: { hidden?: boolean; applied?: string; planShown?: { what: string; rows: PlanChange['rows']; note?: string } },
): Talk => ({
  ...talk,
  turns: talk.turns.map((t, n) => (
    n === i && t.who === 'skoka' && t.kind === 'answer' ? { ...t, ...patch } : t
  )),
});

/** Зачем открыли окно: посмотреть разбор или задать вопрос. */
export type AskIntent =
  /** Разбор картины: считается сразу. */
  | { mode: 'review' }
  /**
   * Вопрос. Готовый вопрос (из наблюдения на главной) только подставляется в
   * поле: отправляет его человек сам. Иначе одно нажатие «Что можно сделать»
   * тратило вопрос из лимита, о чём кнопка не говорила.
   */
  | { mode: 'ask'; question?: string };

export function AskModal({
  state, talk, setTalk, onClose, onApplied, onEdit, onDocs, onClear, onPlans, onSignup, intent,
}: {
  state: AppState;
  talk: Talk;
  setTalk: (t: Talk) => void;
  onClose: () => void;
  /** План изменился: главная обязана это показать. Окно остаётся открытым. */
  onApplied: () => void;
  /** «Записать покупку»: форма расхода с суммой, датой и счётом на проверку. */
  onEdit: (p: Proposal) => void;
  /** Ограничения расчётов и ИИ. */
  onDocs?: () => void;
  /** Забыть все разговоры. */
  onClear?: () => void;
  /** Тарифы: вопросы кончились или нужен Pro. */
  onPlans?: () => void;
  /** Нужен аккаунт. */
  onSignup?: () => void;
  /** Зачем открыли окно. */
  intent: AskIntent;
}) {
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(talk.draft ?? '');
  const [reading, setReading] = useState<PastTalk | null>(null);
  const [terms, setTerms] = useState<Extract<Proposal, { kind: 'purchase' }> | null>(null);
  const [about, setAbout] = useState(false);
  const [access, setAccess] = useState<AccessState | null>(null);
  const started = useRef(false);
  const latest = useRef(state);
  latest.current = state;
  /** Последнее состояние разговора — для отложенных действий. */
  const talkRef = useRef(talk);
  talkRef.current = talk;
  const draftRef = useRef(draft);
  draftRef.current = draft;

  useEffect(() => { void fetchAccess().then(setAccess); }, []);
  // Включили пробный или оплатили — метки «Pro» снимаются сразу, без перезагрузки.
  useEffect(() => onAccessChanged(() => { void fetchAccess().then(setAccess); }), []);
  // Недописанный вопрос переживает закрытие окна.
  useEffect(() => () => {
    if ((talkRef.current.draft ?? '') !== draftRef.current) setTalk({ ...talkRef.current, draft: draftRef.current });
  }, []);

  const ask = async (
    question: string | undefined, answering: string | undefined, from: Talk, review = false,
  ): Promise<void> => {
    setBusy(true);
    setDraft('');
    // Разбор человек просил кнопкой: в переписке его просьба видна словами,
    // чтобы было ясно, на что этот ответ.
    const asked: Turn[] = question
      ? [{ who: 'you', text: question, at: now() }]
      : review ? [{ who: 'you', text: REVIEW_ASK, at: now(), review: true }] : [];
    const reply = Boolean(answering && question);
    const original = reply ? from.asked ?? question : question;
    const { asked: _was, ...base } = from;
    const asking: Talk = {
      ...base,
      draft: '',
      turns: [...from.turns, ...asked],
      ...(original ? { asked: original } : {}),
      ...(reply
        ? { history: [...from.history, { question: answering!, answer: question! }] }
        : {}),
    };
    setTalk(asking);
    // В отчёт — сам факт вопроса и был ли это первый разбор, без текста.
    track('ask_sent', { kind: original ? 'question' : 'review' });

    const out = await askAdvisor({
      state,
      ...(original ? { question: original } : {}),
      ...(asking.analysisId ? { analysisId: asking.analysisId } : {}),
      history: asking.history,
      currentVersion: () => stateVersion(latest.current),
    });
    setBusy(false);
    // Счётчик вопросов меняется с каждым ответом.
    void fetchAccess().then(setAccess);

    if (out.kind === 'stale') {
      setTalk({
        ...asking,
        turns: [...asking.turns, {
          who: 'skoka', kind: 'note', at: now(),
          text: 'Данные изменились, пока я считал. Спроси ещё раз — посчитаю заново.',
        }],
      });
      return;
    }
    if (out.kind === 'failed') {
      setTalk({
        ...asking,
        turns: [...asking.turns, out.refused
          // Отказ по доступу повторять незачем: что делать — в поле ввода.
          ? { who: 'skoka', kind: 'note', at: now(), text: out.refused.message || 'Вопросы сейчас недоступны.' }
          : {
            who: 'skoka', kind: 'note', at: now(),
            text: 'Не удалось получить ответ. Твой вопрос сохранён.',
            retry: {
              ...(question ? { question } : {}),
              ...(answering ? { answering } : {}),
              ...(review ? { review } : {}),
            },
          }],
      });
      return;
    }
    if (out.kind === 'question') {
      setTalk({
        ...asking,
        analysisId: out.analysisId,
        turns: [...asking.turns, {
          who: 'skoka', kind: 'question', text: out.question, at: now(),
          ...(out.options ? { options: out.options } : {}),
        }],
      });
      return;
    }
    // Ответ оплачен: следующий вопрос — новый разбор. Старый номер слать
    // дальше нельзя — сервер заводил бы новый на каждом раунде расчётов.
    const { analysisId: _paid, asked: _done, ...rest } = asking;
    setTalk({
      ...rest,
      history: [],
      turns: [...asking.turns, {
        who: 'skoka', kind: 'answer', answer: out.answer, version: out.stateVersion, at: now(),
        ...(out.proposal ? { proposal: out.proposal } : {}),
        ...(review ? { review: true } : {}),
      }],
    });
  };

  /*
    То, ради чего окно открыли, делается один раз.

    Вчерашний разговор уходит в историю: над новым вопросом не висит полный
    старый разбор. Разбор на тех же данных второй раз не считается — он вышел
    бы таким же, а стоил бы ещё одной единицы.
  */
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const version = stateVersion(latest.current);
    const sameReview = talk.turns.some(
      (t) => t.who === 'skoka' && t.kind === 'answer' && t.review && t.version === version,
    );
    const base = intent.mode === 'review' && sameReview
      ? talk
      : isOld(talk, state.today) ? archiveTalk(talk) : talk;
    if (base !== talk) setTalk(base);
    if (intent.mode === 'review') {
      if (!sameReview) void ask(undefined, undefined, base, true);
      return;
    }
    if (intent.question) setDraft(intent.question);
  }, [intent]);

  const pending = talk.turns[talk.turns.length - 1];
  const answering = pending && pending.who === 'skoka' && pending.kind === 'question'
    ? pending.text : undefined;

  const version = stateVersion(state);
  const ctx = useMemo(() => contextOf(state), [version]);
  const examples = useMemo(() => examplesFor(ctx), [ctx]);

  const turns = reading ? reading.turns : talk.turns;
  const messages = useMemo(
    () => toMessages(turns, state, version, !reading),
    [turns, state, version, reading],
  );

  /** Вопрос, на который дан ответ `i`: для «Пересчитать». */
  const questionFor = (i: number): Turn | undefined => {
    for (let n = i - 1; n >= 0; n -= 1) {
      const t = talk.turns[n]!;
      if (t.who === 'you') return t;
    }
    return undefined;
  };
  const turnIndex = (id: string) => Number(id.slice(1));

  const apply = async (i: number) => {
    const t = talk.turns[i];
    if (!t || t.who !== 'skoka' || t.kind !== 'answer' || t.proposal?.kind !== 'goal_change') return;
    // Проверка актуальности — последнее перед записью: расчёт мог устареть,
    // пока человек думал.
    if (stateVersion(latest.current) !== t.version || t.applied) return;
    const patch = goalPatch(t.proposal);
    const shown = goalPlan(t.proposal, t, latest.current, t.version);
    setBusy(true);
    await updateGoal(t.proposal.goalId, patch);
    await keepMonthPlan(await loadState());
    setBusy(false);
    setTalk(patchAt(talkRef.current, i, {
      applied: now(),
      ...(shown ? { planShown: { what: shown.what, rows: shown.rows, ...(shown.note ? { note: shown.note } : {}) } } : {}),
    }));
    onApplied();
  };

  const retryFrom = (i: number) => {
    const note = talk.turns[i + 1];
    if (!note || note.who !== 'skoka' || note.kind !== 'note' || !note.retry) return null;
    const before = talk.turns.slice(0, i);
    // Уточнение, записанное неудавшимся ходом, не удваиваем.
    const pair = talk.history.at(-1);
    const history = note.retry.answering && pair?.question === note.retry.answering
      ? talk.history.slice(0, -1) : talk.history;
    return { retry: note.retry, from: { ...talk, turns: before, history } };
  };

  return (
    <>
      <ChatSheet
        today={state.today}
        messages={messages}
        busy={busy}
        draft={draft}
        setDraft={setDraft}
        access={chatAccess(access)}
        past={(talk.past ?? []).map((p) => pastRow(p, state.today))}
        {...(reading ? { reading: { date: dayMonth(localDay(reading.at), state.today), onBack: () => setReading(null) } } : {})}
        {...(answering ? { answering: true } : {})}
        examples={examples}
        {...(lastReviewAt(talk) ? { lastReview: dayMonth(localDay(lastReviewAt(talk)!), state.today) } : {})}
        on={{
          onReview: () => { void ask(undefined, undefined, talkRef.current, true); },
          onSend: (text) => { void ask(text, answering, talkRef.current); },
          onRetry: (id) => {
            const r = retryFrom(turnIndex(id));
            if (r) void ask(r.retry.question, r.retry.answering, r.from, r.retry.review);
          },
          onEditQuestion: (id) => {
            const i = turnIndex(id);
            const t = talk.turns[i];
            const r = retryFrom(i);
            if (!t || t.who !== 'you' || !r) return;
            setTalk(r.from);
            if (!t.review) setDraft(t.text);
          },
          onEditConditions: (id) => {
            const t = talk.turns[turnIndex(id)];
            if (t && t.who === 'skoka' && t.kind === 'answer' && t.proposal?.kind === 'purchase') setTerms(t.proposal);
          },
          onPlan: () => {},
          onConfirmPlan: (id) => { void apply(turnIndex(id)); },
          onLaterPlan: (id) => setTalk(patchAt(talk, turnIndex(id), { hidden: true })),
          onRecalc: (id) => {
            const q = questionFor(turnIndex(id));
            if (!q || q.who !== 'you') return;
            void ask(q.review ? undefined : q.text, undefined, talkRef.current, q.review);
          },
          onRecord: (id) => {
            const t = talk.turns[turnIndex(id)];
            if (t && t.who === 'skoka' && t.kind === 'answer' && t.proposal) onEdit(t.proposal);
          },
          onNew: () => { setReading(null); setTalk(archiveTalk(talk)); },
          onClear: () => { setReading(null); onClear?.(); },
          onDocs: () => setAbout(true),
          onOpenPast: (id) => setReading((talk.past ?? []).find((p) => p.id === id) ?? null),
          onAskAgain: (text) => { setReading(null); setDraft(text); },
          onAccessCta: () => {
            const gate = access ? advisorGate(access) : null;
            if (gate?.kind === 'signup') onSignup?.(); else onPlans?.();
          },
          onClose,
        }} />
      {/*
        «На чём основан ответ» — короткой справкой здесь же, а не переходом к
        полотну условий и согласий: оттуда человек возвращался через профиль.
      */}
      {about && (
        <SheetWrap title="О расчётах и ограничениях" onClose={() => setAbout(false)}>
          <div className="chat-about">
            <p>Числа считает приложение по твоим данным: счетам, платежам, доходам и обычным тратам.
              ИИ объясняет расчёт словами и предлагает следующий шаг.</p>
            <p>Прогноз — расчёт, а не обещание: доход может прийти позже или меньше, траты — оказаться другими.</p>
            <p>Языковая модель ошибается, и её ответ — не индивидуальная финансовая, юридическая или налоговая рекомендация.</p>
            <p>Разговор ничего не меняет в данных: план меняется только кнопкой в карточке ответа.</p>
          </div>
          <button className="btn" onClick={() => setAbout(false)}>Понятно</button>
          {onDocs && (
            <button className="lnk wide" onClick={() => { setAbout(false); onDocs(); }}>Условия, данные и согласия</button>
          )}
        </SheetWrap>
      )}
      {terms && (
        <ConditionsSheet state={state}
          value={{ amount: terms.amount, date: terms.date ?? state.today, ...(terms.card ? { card: terms.card } : {}) }}
          onClose={() => setTerms(null)}
          onApply={(q) => { setTerms(null); void ask(q, undefined, talkRef.current); }} />
      )}
    </>
  );
}

/** Изменение цели из предложения — то, что запишется по «Применить к плану». */
function goalPatch(p: Extract<Proposal, { kind: 'goal_change' }>): Partial<Goal> {
  return {
    ...(p.monthlyContribution !== undefined
      ? {
        monthlyContribution: p.monthlyContribution,
        contributionMode: p.monthlyContribution > ZERO ? 'fixed' as const : 'residual' as const,
      }
      : {}),
    ...(p.targetDate ? { targetDate: p.targetDate } : {}),
  };
}

/** Строка истории: дата, тема, короткий вывод. */
function pastRow(p: PastTalk, today: string): PastRow {
  const ask = p.turns.find((t) => t.who === 'you');
  const answer = [...p.turns].reverse().find((t) => t.who === 'skoka' && t.kind === 'answer');
  const short = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);
  return {
    id: p.id,
    date: dayMonth(localDay(p.at), today),
    topic: short(ask && ask.who === 'you' ? (ask.review ? 'Разбор твоих денег' : ask.text) : 'Разбор твоих денег', 60),
    summary: answer && answer.who === 'skoka' && answer.kind === 'answer'
      ? short(answer.answer.headline, 80)
      : 'Без ответа',
  };
}

/**
 * Доступ к вопросам — словами для поля ввода.
 *
 * Кончились вопросы или нужен Pro — поле заменяет объяснение, что делать
 * дальше и что осталось бесплатным. Пока вопросов много, счётчик только в
 * меню: напоминание о лимите там, где он далеко, мешает спрашивать.
 */
export function chatAccess(a: AccessState | null): ChatAccess {
  if (!a || a.offline) return { kind: 'open' };
  const free = 'Проверка покупки, календарь платежей и записи работают как обычно.';
  // У платящего — свой счётчик. Общая проверка доступа отвечала бы ему
  // «первый разбор бесплатно», когда пакет кончился.
  if (a.tier !== 'pro' && a.tier !== 'trial') {
    const gate = advisorGate(a);
    if (gate.kind === 'offer') {
      // Сразу после бесплатного разбора — самый сильный момент: человек только
      // что увидел пользу. Говорим, что было и что дальше, без «доступ закрыт».
      return { kind: 'locked', title: 'Это был бесплатный разбор', text: `Дальше — вопросы SKOKA в любой день и новые разборы в Pro. ${free}`, cta: 'Что даёт Pro' };
    }
    if (gate.kind === 'signup') return { kind: 'locked', title: 'Нужен аккаунт', text: gate.note, cta: 'Создать аккаунт' };
    if (gate.kind === 'first') return { kind: 'open', left: 'Первый разбор — бесплатно' };
    return { kind: 'open' };
  }
  const { used, left } = a.analysis;
  const total = used + left;
  const trial = a.tier === 'trial';
  if (left <= 0) {
    return trial
      ? { kind: 'locked', title: 'Вопросы пробного периода закончились', text: free, cta: 'Подключить Pro' }
      : { kind: 'locked', title: 'Вопросы на этот месяц закончились', text: `Новые будут ${a.resetsOn ? dayLabel(a.resetsOn) : 'в следующем месяце подписки'}. ${free}` };
  }
  const word = pluralWord(left, 'вопрос', 'вопроса', 'вопросов');
  if (left <= 3) return { kind: 'low', text: `Осталось ${left} ${word} из ${total}` };
  return { kind: 'open', left: `Осталось ${left} из ${total} вопросов${trial ? ' пробного периода' : ' в этом месяце'}` };
}

/** Реплики разговора — сообщения окна. */
export function toMessages(turns: Turn[], state: AppState, version: string, live: boolean): ChatMsg[] {
  const out: ChatMsg[] = [];
  turns.forEach((t, i) => {
    const id = `t${i}`;
    const at = t.at ?? '';
    if (t.who === 'you') {
      const next = turns[i + 1];
      const failed = next?.who === 'skoka' && next.kind === 'note' && Boolean(next.retry);
      out.push({ id, who: 'you', text: t.text, at, ...(failed ? { failed: true } : {}) });
      return;
    }
    if (t.kind === 'note') {
      // Неудача хода показана у вопроса, к которому она относится.
      if (t.retry && turns[i - 1]?.who === 'you') return;
      out.push({ id, who: 'skoka', kind: 'text', text: t.text, at });
      return;
    }
    if (t.kind === 'question') {
      out.push({ id, who: 'skoka', kind: 'clarify', text: t.text, at, ...(t.options ? { replies: t.options } : {}) });
      return;
    }
    out.push(answerMessage(id, t, state, version, live));
  });
  return out;
}

function answerMessage(
  id: string, t: Extract<Turn, { kind: 'answer' }>, state: AppState, version: string, live: boolean,
): ChatMsg {
  const at = t.at ?? '';
  const view = chatAnswerOf(t.answer);
  // Короткий ответ — обычным сообщением.
  if ('text' in view) return { id, who: 'skoka', kind: 'text', text: view.text, at };
  const { answer } = view;
  const p = t.proposal;
  const plan = live && p?.kind === 'goal_change' && !t.hidden ? goalPlan(p, t, state, version) : null;
  return {
    id, who: 'skoka', kind: 'answer', answer, at,
    // Покупку можно пересчитать на других условиях или записать, если она
    // уже случилась. Добавить в план нельзя: запланированных покупок в
    // приложении пока нет.
    ...(live && p?.kind === 'purchase' ? { actions: { edit: true, record: true } } : {}),
    ...(plan ? { plan } : {}),
  };
}

/** Карточка «Что изменится» для изменения цели. */
function goalPlan(
  p: Extract<Proposal, { kind: 'goal_change' }>, t: Extract<Turn, { kind: 'answer' }>,
  state: AppState, version: string,
): PlanChange | null {
  if (t.applied && t.planShown) {
    return {
      ...t.planShown, confirm: 'Применить к плану', state: 'applied', appliedAt: clock(t.applied),
    };
  }
  const goal = state.goals.find((g) => g.id === p.goalId);
  if (!goal) return null;
  const effect = previewGoalChange({
    today: state.today,
    goals: state.goals,
    change: { goalId: goal.id, patch: goalPatch(p) },
    incomeStreams: state.incomeStreams,
    obligations: state.obligations,
    accounts: state.accounts,
    creditTerms: state.creditTerms,
    loans: state.loans,
    needBaseline: state.needBaseline,
    mode: state.planMode ?? 'balanced',
  });
  const date = (d: PlainDate | null | undefined, none: string) => (d ? dayMonthYear(d) : none);
  const before = effect.before.find((g) => g.goalId === goal.id);
  const after = effect.after.find((g) => g.goalId === goal.id);
  const money = (m: Money) => formatMoney(m);
  const rows: PlanChange['rows'] = [
    ...(p.monthlyContribution !== undefined
      ? [{ label: 'Взнос в месяц', before: money(goal.monthlyContribution), after: money(p.monthlyContribution) }]
      : []),
    ...(p.targetDate ? [{ label: 'Срок', before: date(goal.targetDate, 'без срока'), after: dayMonthYear(p.targetDate) }] : []),
    // Срок — только когда он посчитан: «не накопится → не накопится» ничего
    // не говорит, а почему так — сказано в пояснении ниже.
    ...(before?.eta || after?.eta
      ? [{ label: 'Накопится', before: date(before?.eta, 'не накопится'), after: date(after?.eta, 'не накопится'), worse: Boolean(before?.eta && (!after?.eta || after.eta > before.eta)) }]
      : []),
    ...effect.consequences.filter((c) => c.goalId !== goal.id)
      .map((c) => ({ label: `Цель «${c.name}»`, after: c.text, worse: c.worse })),
  ];
  // Что меняется — в строках ниже; здесь только о чём речь.
  const what = `Цель «${goal.name}»`;
  return {
    what, rows, confirm: 'Применить к плану',
    ...(effect.limits.length ? { note: `${effect.limits.join('. ')}.` } : {}),
    state: t.applied ? 'applied' : t.version !== version ? 'stale' : 'offer',
    ...(t.applied ? { appliedAt: clock(t.applied) } : {}),
    ...(t.version !== version && !t.applied ? { staleReason: 'Данные изменились после этого расчёта.' } : {}),
  };
}

/** Сумма для формы покупки: её заполняет вызывающий экран. */
export const purchasePrefill = (
  p: Proposal,
): { amount: Money; date?: PlainDate; accountId?: string } | null =>
  (p.kind === 'purchase'
    ? { amount: p.amount, ...(p.date ? { date: p.date } : {}), ...(p.card ? { accountId: p.card } : {}) }
    : null);
